import {
  PrinterFeatures,
  PrinterFeaturesList,
} from '@monetization/hpaip-components-library';
import { BundleProductWithLowestPrice } from '@storefront/app/pdp-config/printer/product-configuration/product-configuration.utils';
import sendOpenTelemetryEvent from '@storefront/utils/sendOpenTelemetryEvent';
import {
  IconWifi100,
  IconTopCoverUnlock,
  IconSmartApp,
  IconRemoteDesktop,
  IconPrinter,
  IconFax,
  IconCamera,
} from '@veneer/core';

interface PrinterFeaturesJSON {
  printCopyScan: boolean;
  wirelessCapability: boolean;
  dimensions: {
    width: number;
    depth: number;
    height: number;
    unit: string;
  };
  inkType: string;
  printSpeed: number;
  paperInput: number;
  photoTray: boolean;
  lcd: boolean;
  memoryCard: boolean;
  builtInFax: boolean;
  autoDocFeeder: boolean;
  adfCapacity: number;
  ethernetNetworking: boolean;
  aiEnhanced: boolean;
}

const features = {
  inkType: (type: string) => ({
    id: 'inkType',
    name: `Ink ${type} printer`,
  }),
  printCopyScan: {
    id: 'printCopyScan',
    name: 'Print, copy, scan',
    icon: <IconSmartApp size={28} />,
  },
  dimensions: (dimension: string) => ({
    id: 'dimensions',
    name: '(W x D x H)',
    value: dimension,
  }),
  wirelessCapability: {
    id: 'wirelessCapability',
    name: 'Wireless capability',
    icon: <IconWifi100 size={28} />,
  },
  ethernetNetworking: {
    id: 'ethernetNetworking',
    name: 'Ethernet networking',
  },
  aiEnhanced: {
    id: 'aiEnhanced',
    name: 'AI Enhanced',
  },
  printSpeed: (speed: number) => ({
    id: 'printSpeed',
    name: 'Print speed, max ppm',
    value: speed,
  }),
  paperInput: (speed: number) => ({
    id: 'paperInput',
    name: 'Paper input, sheets',
    value: speed,
  }),
  photoTray: {
    id: 'photoTray',
    name: 'Photo tray',
    icon: <IconCamera size={28} />,
  },
  lcd: {
    id: 'lcd',
    name: 'LCD touch screen',
    icon: <IconRemoteDesktop size={28} />,
  },
  memoryCard: {
    id: 'memoryCard',
    name: 'USB memory card host',
    icon: <IconTopCoverUnlock size={28} />,
  },
  builtInFax: {
    id: 'builtInFax',
    name: 'Built-in fax',
    icon: <IconFax size={28} />,
  },
  autoDocFeeder: {
    id: 'autoDocFeeder',
    name: 'Auto document feeder',
    icon: <IconPrinter size={28} />,
  },
  adfCapacity: (adfCapacity: number) => ({
    id: 'adfCapacity',
    name: 'ADF capacity, sheets',
    value: adfCapacity,
  }),
};

export const featuresKeys = Object.keys(features);

export const splitSKU = (sku: string): string => sku.split('.')[0];

const printersOverrides: Partial<PrinterFeaturesList>[] = [
  {
    id: 'Sayan',
    overrides: {
      lcd: {
        name: 'Touch display',
      },
    },
  },
  {
    id: 'SayanOnbooks',
    overrides: {
      lcd: {
        name: 'Touch display',
      },
    },
  },
  {
    id: 'SmartTankOnbooks',
    overrides: {
      lcd: {
        name: 'Touch display',
      },
    },
  },
];

const applyOverrides = (
  features: PrinterFeatures[],
  overrides?: Record<string, Partial<PrinterFeatures>>
) => {
  if (!overrides) return features;

  return features.map((feature) => ({
    ...feature,
    ...(overrides[feature.id] || {}),
  }));
};

const createFeaturesArray = (specs: PrinterFeaturesJSON): PrinterFeatures[] => {
  const {
    printCopyScan,
    wirelessCapability,
    dimensions,
    inkType,
    printSpeed,
    paperInput,
    photoTray,
    lcd,
    memoryCard,
    builtInFax,
    autoDocFeeder,
    adfCapacity,
    ethernetNetworking,
    aiEnhanced,
  } = specs;

  const printerFeatures = [
    printCopyScan && features.printCopyScan,
    wirelessCapability && features.wirelessCapability,
    dimensions &&
      features.dimensions(
        `${dimensions.width} x ${dimensions.depth} x ${dimensions.height} ${dimensions.unit}`
      ),
    inkType && features.inkType(inkType),
    printSpeed && features.printSpeed(printSpeed),
    paperInput && features.paperInput(paperInput),
    photoTray && features.photoTray,
    lcd && features.lcd,
    aiEnhanced && features.aiEnhanced,
    memoryCard && features.memoryCard,
    builtInFax && features.builtInFax,
    autoDocFeeder && features.autoDocFeeder,
    adfCapacity && features.adfCapacity(adfCapacity),
    ethernetNetworking && features.ethernetNetworking,
  ].filter(Boolean);

  return printerFeatures;
};

const trackFeatureDataValidation = (
  sku: string,
  specs: PrinterFeaturesJSON
) => {
  const missingKeys = featuresKeys.filter((key) => !(key in specs));
  if (missingKeys.length > 0) {
    const name = 'specifications-json-missing-properties';
    const message = `Missing properties on hp_specifications_json (Modal Printer Comparison) object for SKU "${sku}". Missing properties: ${missingKeys}`;
    sendOpenTelemetryEvent(name, message);
  }

  const invalidData = Object.entries(specs).filter(
    ([_, value]) => value === null || value === undefined
  );

  if (invalidData.length > 0) {
    const name = 'specifications-json-invalid-data';
    const message = `Missing or invalid hp_specifications_json (Modal Printer Comparison) object for SKU "${sku}". Invalid attributes: ${invalidData}`;
    sendOpenTelemetryEvent(name, message);
  }
};

export const printersFeaturesList = (
  data: BundleProductWithLowestPrice[]
): PrinterFeaturesList[] => {
  const printerList = data;
  if (!printerList || !printerList.length) return [];

  return printerList.map((item) => {
    const specsString: string | null = item.items
      .flatMap((productItem) => productItem.options)
      .find((option) => option.product?.hp_specifications_json)
      ?.product?.hp_specifications_json;

    if (!specsString) {
      console.warn(`No hp_specifications_json found for SKU "${item.sku}"`);
      return;
    }

    const specs: PrinterFeaturesJSON = JSON.parse(specsString);
    trackFeatureDataValidation(item.sku, specs);
    const printerFeatures = createFeaturesArray(specs);
    const printerOverrides = printersOverrides.find(
      (o) => o.id === splitSKU(item.sku)
    )?.overrides;

    return {
      id: item.sku,
      features: applyOverrides(printerFeatures, printerOverrides),
    };
  });
};
